import axios from "axios";
import type { Check } from "./check";

export type PricelistStatus = "active" | "archived";
export type PaymentPeriod = "month" | "quarter" | "year";
export type CheckTypeKey = "siteCheck" | "policyCheck" | "it";

export type TariffLimitPeriod = "day" | "week" | "month";
export type LimitPeriod = "day" | "week" | "month" | "year";

export interface PriceItem {
  period: PaymentPeriod;
  price: number;
}

export interface CheckTypeOptions {
  schedule?: boolean;
  report?: boolean;
}

export interface CheckTypeLimit {
  period: PaymentPeriod;
  count: number;
}

export interface CheckTypeConfig {
  enabled: boolean;
  categoryIds?: string[];
  limits?: CheckTypeLimit[];
  options?: CheckTypeOptions;
}

export type PricelistCheckTypes = {
  siteCheck: CheckTypeConfig;
  policyCheck: CheckTypeConfig;
  it: CheckTypeConfig;
};

export interface PricelistPrice {
  periods: PriceItem[];
  availablePeriods: PaymentPeriod[];
}

export interface TariffItem {
  period: TariffLimitPeriod;
  limit: number;
  categoryId: string;
}

export interface UsageSetting {
  period: LimitPeriod;
  limit: number;
}

export interface UsageConsumption {
  period: LimitPeriod;
  limit: number;
  counter: number;
}

export interface PolicyCheckSettings {
  checks?: Check[];
  usageSettings?: UsageSetting[];
  usageConsumption?: UsageConsumption[];
}

export interface Pricelist {
  id?: string;
  name?: string;
  description?: string;
  status?: PricelistStatus;
  validFrom?: string;
  validTo?: string;
  visibleForUsers?: boolean;
  checkTypes?: Partial<PricelistCheckTypes> | PricelistCheckTypes;
  price?: Partial<PricelistPrice> | PricelistPrice;
  priceItemList?: PriceItem[];
  siteAmount?: number;
  emailNotification?: boolean;
  scanByScheduleEnabled?: boolean;
  policyCheckEnabled?: boolean;
  policyCheckSettings?: PolicyCheckSettings;
  tariffItemList?: TariffItem[];
}

export const CHECK_TYPE_KEYS: readonly CheckTypeKey[] = [
  "siteCheck",
  "policyCheck",
  "it",
] as const;

export const CHECK_TYPE_LABELS: Record<CheckTypeKey, string> = {
  siteCheck: "Проверка сайтов",
  policyCheck: "Проверка политики",
  it: "Проверка минцифры",
};

export const fetchPricelistList = (includeHidden = false): Promise<Pricelist[]> =>
  axios
    .get("/pricelist", { params: includeHidden ? { includeHidden: true } : undefined })
    .then((resp) => (Array.isArray(resp.data) ? resp.data : []));

export const fetchPricelistById = (id: string): Promise<Pricelist> =>
  axios.get(`/pricelist/${id}`).then((resp) => resp.data);

export const createPricelist = (data: Pricelist): Promise<Pricelist> =>
  axios.post("/pricelist", data).then((resp) => resp.data);

export const updatePricelist = (id: string, data: Pricelist): Promise<Pricelist> =>
  axios.put(`/pricelist/${id}`, data).then((resp) => resp.data);
